import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar, { SidebarNav, useNavItems } from "./Sidebar";
import OrgSwitcher from "./OrgSwitcher";
import UserMenu from "./UserMenu";
import NotificationBell from "./NotificationBell";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Menu, ShieldCheck } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";

// J-1: на мобильном сайдбар скрыт — без этого drawer'а телефон не имеет
// навигации вообще (пользователь заперт на текущей странице).
function MobileNavDrawer() {
  const [open, setOpen] = useState(false);
  const { navItems } = useNavItems();
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="mobile-nav-burger"
        aria-label="Открыть меню"
        className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-zinc-600 hover:bg-zinc-100 shrink-0"
      >
        <Menu className="h-5 w-5" strokeWidth={1.8} />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-parchment flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-2 text-left">
            <SheetTitle className="flex items-center gap-2.5">
              <span className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.2} />
              </span>
              <span className="text-lg font-semibold tracking-tight text-zinc-900">
                Signa
              </span>
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">
            <SidebarNav
              navItems={navItems}
              onNavigate={() => setOpen(false)}
              testidPrefix="mobile-"
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// Command palette only mounts when the user invokes Cmd+K; deferring its
// import shaves cmdk (~6 KB) and its search index off the initial bundle.
const CommandPalette = lazy(() => import("./CommandPalette"));

// `#RRGGBB` only — we never trust org settings to inject arbitrary CSS.
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

function hexToHslTriplet(hex) {
  // Convert "#0066CC" → "210 100% 40%" so it slots into the existing
  // `--primary: <triplet>` token used throughout tailwind.config.js.
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default function Layout() {
  const location = useLocation();
  const { currentOrg } = useAuth();
  const { t } = useTranslation();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global Cmd+K / Ctrl+K shortcut. Listener attaches once on mount.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Apply per-org primary color as a CSS variable override.
  useEffect(() => {
    const color = currentOrg?.settings?.primary_color;
    const root = document.documentElement;
    if (color && HEX_COLOR_RE.test(color)) {
      root.style.setProperty("--primary", hexToHslTriplet(color));
    } else {
      root.style.removeProperty("--primary");
    }
    return () => {
      root.style.removeProperty("--primary");
    };
  }, [currentOrg?.settings?.primary_color]);

  const titles = useMemo(
    () => ({
      "/": t("nav.dashboard"),
      "/inbox": t("nav.inbox"),
      "/documents": t("nav.documents"),
      "/templates": t("nav.templates"),
      "/contractors": t("nav.contractors"),
      "/upload": t("documents.upload"),
      "/team": t("nav.team"),
      "/settings/organization": t("settings.organization"),
      "/settings/audit": t("settings.audit"),
      "/settings/notifications": t("settings.notifications"),
      "/notifications": t("nav.notifications"),
      "/reports": t("nav.reports"),
      "/forms": t("nav.forms"),
      "/workflows": t("nav.workflows"),
    }),
    [t],
  );

  let title = titles[location.pathname] || "";
  if (!title) {
    if (location.pathname.startsWith("/documents/")) title = t("nav.documents");
    else if (location.pathname.startsWith("/contractors/"))
      title = t("nav.contractors");
    else if (location.pathname.startsWith("/settings/"))
      title = t("nav.settings");
  }

  return (
    /* Прокручивается КОНТЕНТ, а не вся страница. Раньше скроллился документ
       целиком: сайдбар — обычный flex-элемент, он растягивался вместе с длинной
       страницей, и кнопка «Создать документ» в его подвале уезжала под сгиб —
       на форме создания её нельзя было увидеть, не прокрутив всю форму. */
    <div className="h-screen overflow-hidden flex bg-canvas">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header
          data-testid="app-header"
          className="h-16 shrink-0 border-b border-zinc-900/10 bg-canvas/90 backdrop-blur-md z-30 flex items-center justify-between px-4 md:px-10 gap-2 md:gap-4"
        >
          {/* UX-18/19: `min-w-0` нужен и на этом flex-элементе, иначе он не
              даёт себя сжать, схлопывается в нулевую ширину и текст надзаголовка
              вылезает поверх бургера, колокольчика и переключателя организации.
              `overflow-hidden` страхует от вылезания за пределы шапки. */}
          <div className="min-w-0 flex-1 flex items-center gap-2 md:gap-3">
            <MobileNavDrawer />
            {currentOrg?.settings?.logo_url ? (
              <img
                src={currentOrg.settings.logo_url}
                alt=""
                className="h-8 w-8 rounded-md object-contain bg-white shrink-0"
                data-testid="org-logo"
              />
            ) : null}
            <div className="min-w-0 flex-1 overflow-hidden">
              {/* Название организации и так есть в переключателе справа —
                  на узких экранах прячем, чтобы освободить место заголовку. */}
              <div className="hidden sm:block text-[11px] uppercase tracking-[0.14em] text-zinc-900/40 truncate">
                {currentOrg?.name || "Signa"}
              </div>
              <h1
                className="text-base md:text-lg font-medium tracking-tight text-zinc-900 leading-tight truncate"
                data-testid="page-title"
              >
                {title}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-3 shrink-0">
            <NotificationBell />
            <OrgSwitcher />
            <UserMenu />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="px-6 md:px-10 py-10 max-w-[1400px] w-full mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
      {paletteOpen && (
        <Suspense fallback={null}>
          <CommandPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
          />
        </Suspense>
      )}
      {/* Toaster переехал в App.js: он должен быть один на всё приложение,
          иначе страницы вне Layout (вход, регистрация, подписание) остаются
          без него и их сообщения не показываются вовсе. */}
    </div>
  );
}
